<template>
    <CreatorActionModel ref="actionModal" EntityType="Payment" @reload="onReload" />

    <div class="col-span-full rounded-sm border border-slate-200 bg-white shadow-lg">
        <div class="p-3">
            <div class="">
                <table v-if="!isLoading" class="w-full table-auto">
                    <thead class="rounded-sm bg-slate-50 text-left text-xs uppercase text-slate-400">
                        <tr>
                            <th class="w-7 whitespace-nowrap p-2 pl-3 pl-5">
                                <div class="flex">
                                    <input
                                        v-model="allSelected"
                                        type="checkbox"
                                        class="form-checkbox"
                                        @change="onToggleSelectAll()"
                                    />
                                </div>
                            </th>

                            <th class="whitespace-nowrap p-2" @click="sortByField('creator')">
                                <div class="flex">
                                    <span class="font-semibold">Creator</span>
                                </div>
                            </th>

                            <th class="whitespace-nowrap p-2" @click="sortByField('status')">
                                <div class="flex">
                                    <span class="font-semibold">Status</span>
                                </div>
                            </th>

                            <th class="whitespace-nowrap p-2" @click="sortByField('amount')">
                                <div class="flex">
                                    <span class="font-semibold">Payout</span>
                                </div>
                            </th>

                            <th class="w-7 pl-2 pr-5">
                                <div
                                    class="flex w-full rounded bg-slate-100 text-slate-600 transition duration-200 hover:bg-slate-200"
                                >
                                    <ParticipantAction
                                        :actions="actions"
                                        :creators="selectedCreators"
                                        :show-count="true"
                                        @perform-action="onActionModalOpen"
                                    />
                                </div>
                            </th>
                        </tr>
                    </thead>

                    <tbody
                        class="divide-y divide-slate-200 border-b border-slate-200 text-sm"
                        :class="{ 'pointer-events-none opacity-50': isBusy === true }"
                    >
                        <tr v-for="creator in creators" :key="creator.payment.id" class="group">
                            <td class="whitespace-nowrap px-2 py-3.5 pl-5">
                                <input
                                    v-model="selectedCreators"
                                    type="checkbox"
                                    class="form-checkbox"
                                    :value="creator"
                                />
                            </td>

                            <td class="whitespace-nowrap px-2 py-3.5">
                                <div class="relative flex items-center">
                                    <NamePlate
                                        class="my-auto"
                                        :logo="creator.participant.platform_user_logo"
                                        :name="creator.participant.platform_user_name"
                                        :display-name="creator.participant.platform_user_name"
                                        :platform-id="creator.participant.platform_id"
                                        :platform-user-id="creator.participant.platform_user_id"
                                        :is-drawer-enabled="true"
                                        :participant="creator.participant"
                                        :campaign="campaign"
                                        :modal-data="{
                                            supercampaign_id: campaign.parent_id,
                                            campaign_id: campaign.id,
                                            participant_id: creator.participant.id,
                                        }"
                                    />
                                </div>
                            </td>

                            <td class="whitespace-nowrap px-2 py-3">
                                <div class="flex">
                                    <div v-if="creator.payment.status" class="my-auto flex rounded bg-slate-100">
                                        <span class="my-auto px-2 text-sm font-medium leading-none">
                                            {{
                                                getFromDictionary(
                                                    `finance.payment.status.${creator.payment.status}`,
                                                    'label',
                                                )
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </td>

                            <td class="whitespace-nowrap px-2">
                                <div v-if="creator.payment.amount" class="text-left">
                                    ${{ $numeral(creator.payment.amount).format('0,0.00') }}
                                </div>
                            </td>

                            <td class="w-7 px-2 pr-5">
                                <div class="flex w-full rounded bg-slate-50 transition duration-200 hover:bg-slate-200">
                                    <ParticipantAction
                                        :actions="actions"
                                        :creators="[creator]"
                                        :show-count="false"
                                        @perform-action="onActionModalOpen"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-if="!creators || (!creators.length && !isLoading)" class="w-full p-4 text-center">
                    <p class="text-sm font-medium">No creators to show</p>
                </div>
            </div>
        </div>

        <div class="my-4 ml-4 mr-4">
            <DiscoveryPagination :paging="paging" record-name="payments" @paging-changed="onPagingChange" />
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import NamePlate from '@/components/plates/NamePlate.vue'
import DiscoveryPagination from '@/partials/tables/discovery/DiscoveryPagination.vue'
import CreatorActionModel from '@/partials/modals/CreatorActionModal.vue'
import ParticipantAction from '@/components/ParticipantAction.vue'

export default {
    name: 'ParticipantPaymentTable',

    components: {
        NamePlate,
        DiscoveryPagination,
        ParticipantAction,
        CreatorActionModel,
    },

    props: {
        creators: {
            type: Array,
        },
        isLoading: {
            type: Boolean,
        },
        paging: {
            type: Object,
        },
        actions: {
            type: Array,
        },
        campaign: {
            type: Object,
        },
        activeParentTab: {
            type: Object,
        },
        activeChildTab: {
            type: Object,
        },
        activeTab: {
            type: Object,
        },
        tabs: {
            type: Object,
        },
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'getLanguageFromCode', 'getCountryFromCode']),

        selectedParticipants() {
            return _.map(this.selectedCreators, (creator) => creator.participant)
        },
    },

    emits: ['paging-change', 'reload'],

    data() {
        return {
            selectedCreators: [],
            isBusy: false,
            allSelected: false,
        }
    },

    watch: {
        selectedCreators() {
            this.allSelected = this.isAllSelected()
        },

        creators() {
            const selectedIds = _.map(this.selectedCreators, (creator) => creator.payment.id)
            this.reselect(selectedIds)
        },
    },

    methods: {
        onToggleSelectAll() {
            if (this.creators === 0) {
                return
            }

            const allSelected = this.isAllSelected()

            if (allSelected) {
                this.selectedCreators = []
            } else {
                this.selectedCreators = this.creators
            }
        },

        isAllSelected() {
            const selectedIds = _.map(this.selectedCreators, (creator) => creator.payment.id)
            const unselectedCreators = _.filter(
                this.creators,
                (creator) => selectedIds.indexOf(creator.payment.id) === -1,
            )
            return this.creators.length > 0 && unselectedCreators.length === 0
        },

        sortByField(column) {
            const newPagingProperties = {
                sortby: '',
                sortdir: '',
            }
            if (this.paging.sortby === column && this.paging.sortdir === 'desc') {
                newPagingProperties.sortdir = 'asc'
            } else {
                newPagingProperties.sortdir = 'desc'
            }

            newPagingProperties.sortby = column
            this.$emit('paging-change', newPagingProperties)
        },

        onPagingChange(newPagingProperties) {
            this.$emit('paging-change', newPagingProperties)
        },

        onActionModalOpen(action, creators) {
            this.$refs.actionModal.onActionModalOpen(action, creators)
        },

        onReload() {
            const selectedIds = _.map(this.selectedCreators, (creator) => creator.payment.id)
            this.$emit('reload')
            this.reselect(selectedIds)
        },

        reselect(selectedPaymentIds) {
            this.selectedCreators = _.filter(
                this.creators,
                (creator) => selectedPaymentIds.indexOf(creator.payment.id) !== -1,
            )
        },
    },
}
</script>
